import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';
import { Shift, ApprovalStatus } from '../../components/shiftcard/ShiftCard';

// ========================================
// TYPES
// ========================================
export type PendingShiftsFilter = 'all' | 'thisweek' | 'today';

// Shape of a pending shift as returned by the API. Includes the supervisor-only
// claimedBy fields and the per-person request sequence id used to build a unique key.
type PendingShiftResponse = Shift & {
    requestId?: number | string;
    requestSeqId?: number | string;
    shiftId?: string;
    requestShiftId?: string;
    claimedByUserId?: string;
    claimedByUserNm?: string;
    // API may return requestType instead of shiftType
    requestType?: string;
    requestDt?: string;
    requestDate?: string;
    // New fields for WKR dual approval
    posterSupervisorId?: string;
    posterSupervisorName?: string;
    posterSupervisorApprovalStatus?: ApprovalStatus;
    claimerSupervisorId?: string;
    claimerSupervisorName?: string;
    claimerSupervisorApprovalStatus?: ApprovalStatus;
};

// Loose shape accepted by the card. Mirrors the availableShifts API payload.
export type RawShift = Partial<Shift> & {
    requestId?: number | string;
    shiftId?: string;
    requestShiftId?: string;

    shiftDesc?: string | null;
    skill?: string;
    skillDesc?: string;
    requiredSkillCd?: string;
    requiredSkillDesc?: string;

    siteCd?: string;
    siteName?: string;

    requestDt?: string;
    requestDate?: string;

    requestType?: string;
    requestNotes?: string;
    requiredEmployeeCount?: number | string;
    requiredTeamCd?: string;

    postedByUserNm?: string;
    postByName?: string;

    claimedByUserNm?: string;
    claimByName?: string;
};

interface PendingShiftsState {
    shifts: Shift[];
    loading: boolean;
    error: string | null;
    filter: PendingShiftsFilter;
}

const initialState: PendingShiftsState = {
    shifts: [],
    loading: false,
    error: null,
    filter: 'all',
};

// ========================================
// HELPERS
// ========================================
// Pending shifts can have multiple rows per requestId (one per requested person),
// so we build a composite "{requestId}-{requestSeqId}" id to keep React keys unique.
const deriveId = (shift: PendingShiftResponse): string => {
    const baseRaw = shift.requestId ?? shift.id ?? shift.shiftId ?? shift.requestShiftId;
    const base = baseRaw !== undefined && baseRaw !== null ? String(baseRaw) : '';
    const seq = shift.requestSeqId !== undefined && shift.requestSeqId !== null
        ? String(shift.requestSeqId)
        : '';
    if (base && seq) return `${base}-${seq}`;
    return base || seq;
};

const normalizeShifts = (payload: PendingShiftResponse[]): Shift[] =>
    (payload ?? []).map((s) => ({
        ...s,
        id: deriveId(s),
        date: s.date ?? s.requestDt ?? s.requestDate ?? '',
        status: 'PND',
        // Map shiftType from requestType if shiftType is not present
        shiftType: s.shiftType ?? s.requestType,
        claimedBy: s.claimedByUserNm ?? s.claimedBy,
        claimedById: s.claimedByUserId ?? s.claimedById,
        // Map dual approval fields for WKR shifts
        dualApproval: (s.shiftType ?? s.requestType) === 'WKR' && s.posterSupervisorApprovalStatus ? {
            posterSupervisorId: s.posterSupervisorId,
            posterSupervisorName: s.posterSupervisorName,
            posterSupervisorApprovalStatus: s.posterSupervisorApprovalStatus,
            claimerSupervisorId: s.claimerSupervisorId,
            claimerSupervisorName: s.claimerSupervisorName,
            claimerSupervisorApprovalStatus: s.claimerSupervisorApprovalStatus,
        } : undefined,
    }));

// ========================================
// ASYNC THUNKS
// ========================================
/**
 * Fetch pending shifts (supervisor view) for a site with optional filter and required skill codes
 * Uses: GET /api/shift/pendingShifts/{siteCd}/{userId}?filter=all|thisweek|today&requiredSkillCds=AAA&requiredSkillCds=BBB
 */
export const fetchPendingShifts = createAsyncThunk(
    'pendingShifts/fetchPendingShifts',
    async (
        {
            siteCd,
            userId,
            filter = 'all',
            requiredSkillCds = [],
        }: { siteCd: string; userId: string; filter?: PendingShiftsFilter; requiredSkillCds?: string[] },
        { rejectWithValue }
    ) => {
        try {
            const params = new URLSearchParams();
            params.append('filter', filter);
            requiredSkillCds.forEach((skillCd) => {
                if (skillCd) {
                    params.append('requiredSkillCds', skillCd);
                }
            });

            const response = await API_FETCH(
                `${ENDPOINTS.GET_PENDING_SHIFTS}/${siteCd}/${userId}?${params.toString()}`
            );
            console.log('✅ Pending Shifts API Response:', response);
            return response as PendingShiftResponse[];
        } catch (error: any) {
            console.error('❌ Pending Shifts API Error:', error);
            return rejectWithValue(error?.message || 'Failed to fetch pending shifts');
        }
    }
);

// ========================================
// SLICE
// ========================================
const pendingShiftsSlice = createSlice({
    name: 'pendingShifts',
    initialState,
    reducers: {
        setFilter: (state, action) => {
            state.filter = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPendingShifts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPendingShifts.fulfilled, (state, action) => {
                state.loading = false;
                state.shifts = normalizeShifts(action.payload);
            })
            .addCase(fetchPendingShifts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { setFilter, clearError } = pendingShiftsSlice.actions;
export default pendingShiftsSlice.reducer;